import {
  BYTES_PER_DRAWCALL,
  BYTES_PER_EDGE_DRAWCALL,
  INDIRECT_DRAW_ALIGNMENT,
  UINTS_PER_DRAWCALL } from
"./IndirectBufferBuilder";
import { MultiDrawRecorder } from "./MultiDrawRecorder";
import { getMaterialTextureMask } from "../main/MaterialUniforms";
import { UniformBuffer } from "../UniformBuffer";
import { UsingMultiDraw } from "../globals";
import { StateTrackingEncoder } from "../StateTrackingEncoder";

/** @import { Renderer } from "../Renderer" */
/** @import { FragmentList } from "../../scene/FragmentList" */
/** @import { RenderBatch } from "../../scene/RenderBatch" */
/** @import { MemoryBlock } from "../../memory/MemoryBlock" */

const CullOffsets = {
  instanceCount: 0,
  drawCallCount: 1,
  batchFirstInstance: 2
};

/**
 * The input array of multiDrawCalls defines a sequence [0,...drawCallCount-1] of drawCalls.
 * This function computes a reverse mapping to find the multiDrawCall for a given index in that drawCall sequence.
 * @param {number} drawCallCount - total number of drawCalls defined by the multiDrawCalls
 * @param {MultiDraw[]} multiDrawCalls
 * @returns {Object} maps
 * @returns {Uint32Array} maps.drawCallToMultiDrawCall
 *     For a given drawCallIndex di result[di] is the index md of the
 *     multiDrawCall that contains this drawCall, i.e., the multiDrawCalls[md]
 *     is the multiDrawCall containing di.
 * @returns {Uint32Array} maps.rangeStartsPerMultiDraw
 *     Index of the first draw call in a multiDrawCall for a given multiDrawCall index.
 */
export function createMultiDrawMaps(drawCallCount, multiDrawCalls) {
  const drawCallToMultiDrawCall = new Uint32Array(drawCallCount);
  const rangeStartsPerMultiDraw = new Uint32Array(multiDrawCalls.length);

  for (let i = 0; i < multiDrawCalls.length; i++) {
    const md = multiDrawCalls[i];
    const rangeStart = md.indirectOffset / BYTES_PER_DRAWCALL;
    const rangeEnd = rangeStart + md.drawCount;
    drawCallToMultiDrawCall.fill(i, rangeStart, rangeEnd);
    rangeStartsPerMultiDraw[i] = rangeStart;
  }
  return {
    drawCallToMultiDrawCall,
    rangeStartsPerMultiDraw
  };
}

/**
 * A MultiDrawBatch contains all GPU data needed to draw a RenderBatch using MultiDrawIndirect calls
 * (or drawindirect(..) calls if multidraw is not supported).
 * @class
 */
export class MultiDrawBatch {

  // Whether this batch runs culling before being drawn.
  enableCulling = true;

  //
  // --- Main GPU data needed for rendering the full RenderBatch ---
  //

  /** @type {MultiDraw[]} Array of MultiDrawCall structs processed for drawing the RenderBatch */
  #multiDraws;

  // Memory blocks in the indirect buffer manager, to be used when drawing.
  // Note: When culling is enabled these will contain the culled draw calls.
  /** @type {MemoryBlock} Draw calls for edge drawing */
  drawCallsBlock;
  /** @type {MemoryBlock} Matching draw calls for edge drawing */
  edgeDrawCallsBlock;

  /** @type {number} Summed instanceCount of all multiDrawCalls in the RenderBatch */
  instanceCount;
  /** @type {number} Summed drawCall count of all multiDrawCalls in the RenderBatch */
  drawCallCount;

  /** @type {Set<Material>} Materials used in the RenderBatch */
  #materials = new Set();

  // # Auxiliary GPUBuffers needed for GPU-side culling

  /** @type {UniformBuffer} */
  cullUniforms;

  // ## Instance Culling Inputs:
  /** @type {GPUBuffer} Contains all draw calls for the RenderBatch */
  drawCallsBuffer;
  /** @type {GPUBuffer|null} */
  instanceToDrawCallBuffer = null;

  // ## Instance Culling Outputs:
  /** @type {GPUBuffer|null} */
  visibleInstanceCountBuffer = null;
  /** @type {GPUBuffer|null} */
  visibleInstanceIdsBuffer = null;

  // ## Draw Culling Inputs:
  /** @type {GPUBuffer|null} */
  edgeDataBuffer = null;
  /** @type {GPUBuffer|null} */
  drawCallToMultiDrawCallBuffer = null;
  /** @type {GPUBuffer|null} */
  rangeStartsPerMultiDrawBuffer = null;

  // ## Draw Culling Outputs:
  /** @type {GPUBuffer|null} */
  drawCallCountsBuffer = null;

  /** @type {GPUBindGroup|null} */
  instanceBg = null;
  /** @type {GPUBindGroup|null} */
  drawCallBg = null;
  /** @type {GPUBindGroup|null} */
  renderBatchBg = null;

  constructor() {}

  /**
   * Init multiDrawBatch from an array of fragment ids. Requirements:
   *  - All fragments must be ready-to-render (geometry and material available and uploaded to GPU)
   *  - fragIds must already be sorted to minimize switches of pipelines, vertexBuffer etc.
   *  - Uniforms must already be uploaded to a range in a batched uniform buffer. This range must provide the uniforms for all fragments listed in the fragIds array.
   * @param {Renderer} renderer
   * @param {FragmentList} fragList
   * @param {number} activeFragCount - Count of fragments with geometries and materials
   * @param {Uint32Array} fragIds - Array of fragment ids.
   * @param {RenderBatch} renderBatch
   * @returns {MultiDrawBatch}
   */
  static fromFragments(renderer, fragList, activeFragCount, fragIds, renderBatch) {
    const mdBatch = new MultiDrawBatch();

    // To set the instanceIDs correctly, we need to know the range of the uniform buffer
    // in advance. More exactly, we have to...
    //  - Allocate the range in advance, but
    //  - Upload the uniforms afterwards, because we have to sort the fragments below
    const renderBatchUniforms = renderer.getRenderBatchUniforms();
    const gpuRange = renderBatchUniforms.getGPUBufferRange(renderBatch);
    const firstInstanceId = gpuRange.startIndex;

    const recorder = new MultiDrawRecorder(renderer.getDevice());
    recorder.start(activeFragCount);

    for (let index = 0; index < fragIds.length; index++) {
      const fragId = fragIds[index];
      const geometry = fragList.getGeometry(fragId);
      const material = fragList.getMaterial(fragId);
      const instanceId = firstInstanceId + index;

      // Skip invalid shapes
      if (!geometry || !material) {
        continue;
      }
      recorder.addFragment(geometry, material, instanceId);
      mdBatch.#materials.add(material);
    };

    const res = recorder.finish(mdBatch.enableCulling, renderer.getIndirectBufferManager());
    mdBatch.#multiDraws = res.multiDraws;
    mdBatch.drawCallsBuffer = res.drawCallsBuffer;
    mdBatch.edgeDataBuffer = res.edgeDataBuffer;
    mdBatch.drawCallsBlock = res.drawCallsBlock;
    mdBatch.edgeDrawCallsBlock = res.edgeDrawCallsBlock;
    mdBatch.instanceToDrawCallBuffer = res.instanceToDrawCallBuffer;
    mdBatch.instanceCount = res.instanceCount;
    mdBatch.drawCallCount = res.drawCallCount;

    if (mdBatch.enableCulling) {
      const objectUniformsBuffer = renderBatchUniforms.getGPUBuffer(renderBatch);
      mdBatch.prepareCullingData(renderer, firstInstanceId, objectUniformsBuffer);
    }

    return mdBatch;
  }

  /**
   * @param {Renderer} renderer
   * @param {number} firstInstanceId
   * @param {GPUBuffer} objectUniformsBuffer
   */
  prepareCullingData(renderer, firstInstanceId, objectUniformsBuffer) {
    const device = renderer.getDevice();

    this.cullUniforms = new UniformBuffer(device, 4, false, false, 'cull uniforms');
    this.cullUniforms.setInt(CullOffsets.instanceCount, this.instanceCount);
    this.cullUniforms.setInt(CullOffsets.drawCallCount, this.drawCallCount);
    this.cullUniforms.setInt(CullOffsets.batchFirstInstance, firstInstanceId);
    this.cullUniforms.upload();

    // Instance Culling Output
    this.visibleInstanceCountBuffer = device.createBuffer({
      label: 'visibleInstanceCountBuffer',
      size: 4 * this.drawCallCount,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
    });
    this.visibleInstanceIdsBuffer = device.createBuffer({
      label: 'visibleInstanceIdsBuffer',
      size: 4 * this.instanceCount,
      usage: GPUBufferUsage.STORAGE
    });

    const cullingEngine = renderer.getGPUCullingEngine();
    this.instanceBg = cullingEngine.createInstanceBindGroup(this, objectUniformsBuffer);

    // Draw Culling Input
    if (UsingMultiDraw()) {
      const {
        drawCallToMultiDrawCall,
        rangeStartsPerMultiDraw
      } = createMultiDrawMaps(this.drawCallCount, this.#multiDraws);

      this.drawCallToMultiDrawCallBuffer = device.createBuffer({
        label: 'drawCallToMultiDrawCallBuffer',
        size: drawCallToMultiDrawCall.byteLength,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
      });
      this.rangeStartsPerMultiDrawBuffer = device.createBuffer({
        label: 'rangeStartsPerMultiDrawBuffer',
        size: rangeStartsPerMultiDraw.byteLength,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
      });
      device.queue.writeBuffer(this.drawCallToMultiDrawCallBuffer, 0, drawCallToMultiDrawCall);
      device.queue.writeBuffer(this.rangeStartsPerMultiDrawBuffer, 0, rangeStartsPerMultiDraw);
    }

    let bufferManager = renderer.getIndirectBufferManager();
    // Draw Culling Output
    this.drawCallsBlock =
    bufferManager.allocate(this.drawCallsBuffer.size, INDIRECT_DRAW_ALIGNMENT);
    this.edgeDrawCallsBlock =
    bufferManager.allocate(this.drawCallsBuffer.size, INDIRECT_DRAW_ALIGNMENT);

    if (UsingMultiDraw()) {
      this.drawCallCountsBuffer = device.createBuffer({
        label: 'drawCallCountsBuffer',
        size: 4 * this.#multiDraws.length,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.INDIRECT
      });
    }

    this.drawCallBg = cullingEngine.createDrawCallBindGroup(this);

    const renderBatchUniforms = renderer.getRenderBatchUniforms();
    this.renderBatchBg = renderBatchUniforms.createBindGroup(
      objectUniformsBuffer, this.visibleInstanceIdsBuffer);
    this._bgLayoutVersion = renderBatchUniforms.getLayoutVersion();

  }

  /**
   * Ensures the material buffer is updated for all of the materials used by
   * this batch.
   * @param {RenderBatchUniforms} renderBatchUniforms
   */
  updateMaterials(renderBatchUniforms) {
    for (const material of this.#materials) {
      const textureMask = getMaterialTextureMask(material);
      renderBatchUniforms.initMaterialUpdateHook(material, textureMask);
    }
  }

  /**
   * Draw MultiDrawBatch
   * @param {StateTrackingEncoder} passEncoder
   * @param {GPUPipeline} pipeline
   */
  draw(passEncoder, pipeline) {
    for (let i = 0; i < this.#multiDraws.length; i++) {
      const multiDraw = this.#multiDraws[i];
      pipeline.drawMulti(passEncoder, multiDraw, this);
    }
  }

  /**
   * @returns {GPUBuffer}
   */
  #destroyBuffer(buffer) {
    if (buffer) {
      buffer.destroy();
    }
  }

  /**
   * @param {Renderer} renderer
   */
  destroy(renderer) {
    // Free memory blocks
    const bufferManager = renderer.getIndirectBufferManager();
    bufferManager.free(this.drawCallsBlock);
    bufferManager.free(this.edgeDrawCallsBlock);

    this.drawCallsBlock = null;
    this.edgeDrawCallsBlock = null;

    // Dispose GPU buffers
    this.#destroyBuffer(this.drawCallsBuffer);
    this.#destroyBuffer(this.instanceToDrawCallBuffer);
    this.#destroyBuffer(this.visibleInstanceCountBuffer);
    this.#destroyBuffer(this.visibleInstanceIdsBuffer);
    this.#destroyBuffer(this.edgeDataBuffer);
    this.#destroyBuffer(this.drawCallToMultiDrawCallBuffer);
    this.#destroyBuffer(this.rangeStartsPerMultiDrawBuffer);
    this.#destroyBuffer(this.drawCallCountsBuffer);

    this.drawCallsBuffer = null;
    this.instanceToDrawCallBuffer = null;
    this.visibleInstanceCountBuffer = null;
    this.visibleInstanceIdsBuffer = null;
    this.edgeDataBuffer = null;
    this.drawCallToMultiDrawCallBuffer = null;
    this.rangeStartsPerMultiDrawBuffer = null;
    this.drawCallCountsBuffer = null;

    this.#multiDraws = null;
    this.instanceCount = 0;
    this.drawCallCount = 0;
    this.#materials = null;
    // TODO: We should add a UniformBuffer.destroy() method and call it here.
    this.cullUniforms = null;
    this.instanceBg = null;
    this.drawCallBg = null;
    this.renderBatchBg = null;
  }

  static computeResourceCost(renderBatch) {
    const fragCount = renderBatch.count;

    // Conservative estimates of drawCall and multiDraw count.
    // Currently, we assume the worst possible case of 1 instance per multidraw. This is usually
    // overestimated and could be improved by some deeper RenderBatch analysis.
    const drawCallCount = renderBatch.count;
    const multiDrawCount = renderBatch.count;

    const drawCallsBufferBytes = drawCallCount * BYTES_PER_DRAWCALL;
    const drawCallsBufferBytesEdges = drawCallCount * BYTES_PER_DRAWCALL;

    const totalBytes =
    drawCallsBufferBytes +
    drawCallsBufferBytesEdges;

    return totalBytes;
  }
}